import { v4 as uuidv4 } from "uuid";
import { useEffect, useRef, useState } from "react";
import { AgGridReact } from "ag-grid-react";
import { GraphQLClient } from "graphql-request";
import { useFieldArray, useFormContext, useWatch } from "react-hook-form";

import { useModal } from "../../../../hooks/use-modal";
import { useDisable } from "../../../../hooks/use-disable";
import { usePurchaseItemListHeader } from "../../../../hooks/Purchase/use-purchase-item-list-header";
import {
  Box,
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from "@mui/material";
import {
  DragDropContext,
  Draggable,
  Droppable,
  DropResult,
} from "react-beautiful-dnd";

import TocIcon from "@mui/icons-material/Toc";
import CloseIcon from "@mui/icons-material/Close";

import ItemNameCell from "./ItemNameCell";
import ButtonLayout from "../../../UI/ButtonLayout";
import Confirmation from "../../../UI/Confirmation";
import ItemTableModal from "../../../UI/Modal/ItemTableModal";
import { CustomizedBox } from "../../../Custom/CustomizedBox";
import CustomizedButton from "../../../Custom/CustomizedButton";
import ControlledSelect from "../../../Controller/ControlledSelect";
import ControlledTextField from "../../../Controller/ControlledTextField";
import CheckedSalesOrderModal from "../../../UI/Modal/CheckedSalesOrderModal";
import ControlledNumberTextField from "../../../Controller/ControlledNumberTextField";

import { ISelectOption } from "../../../../types/global";
import { IPurchaseItemList } from "../../../../types/Purchase";
import { PurchaseDocumentType } from "../../../../generated/general";
import { SalesReferenceDocumentType } from "../../../../generated/purchase";
import { IPurchaseRequest } from "../../../../types/Purchase/purchaseRequest";

import { numberFormatter } from "../../../../utils/Formatter/Global";
import { createGraphQLClientWithMiddleware } from "../../../../services/graphqlClient";
import { ITEM_SKU_DETAIL_AGGRID } from "../../../../services/AgGrid/InventoryAggrid";
import { SalesModelType } from "../../../../generated/sales";
import SubHeader from "../../../Form/Purchase/SubHeader";

interface Props {
  referenceItemList?: IPurchaseItemList[];
  documentType: string;
}

const PurchaseRequestItemList = ({
  documentType,
  referenceItemList,
}: Props) => {
  const {
    control,
    formState: { errors },
    getValues,
    setValue,
  } = useFormContext<IPurchaseRequest>();
  const [disabled] = useDisable();
  const gridRef = useRef<AgGridReact<any>>(null);

  const graphQLClientWithHeaderItem: GraphQLClient =
    createGraphQLClientWithMiddleware("wms");

  const [openDeleteItemConfirmation, setOpenDeleteItemConfirmation] =
    useState<boolean>(false);
  const [deletedIndex, setDeletedIndex] = useState<number | undefined>(
    undefined
  );

  const {
    modal: itemModal,
    openModalHandler: openItemModalHandler,
    closeModalHandler: closeItemModalHandler,
  } = useModal();

  const {
    modal: salesOrderModal,
    openModalHandler: openSalesOrderModalHandler,
    closeModalHandler: closeSalesOrderModalHandler,
  } = useModal();

  const reference_document_list = useWatch({
    control,
    name: "reference_document_list",
  });

  const status = useWatch({
    control,
    name: "aggrid_status",
  });

  const headers = usePurchaseItemListHeader(documentType, disabled, status);

  const [selectedIds, setSelectedIds] = useState<string[]>([]);
  const [idsSnapshot, setIdsSnapshot] = useState<string[]>([]);

  useEffect(() => {
    if (reference_document_list && reference_document_list.length > 0) {
      const uniqueIdList = reference_document_list.map(
        ({ document_unique_id }) => document_unique_id || ""
      );
      setSelectedIds(uniqueIdList);
      setIdsSnapshot(uniqueIdList);
    } else {
      setSelectedIds([]);
      setIdsSnapshot([]);
    }
  }, [reference_document_list]);

  const { fields, move, remove, append } = useFieldArray({
    control,
    name: "item_list",
    keyName: "unique_id",
  });

  const dragEndHandler = (result: DropResult) => {
    if (!result.destination) {
      return;
    }
    move(result.source.index, result.destination.index);
  };

  const openDeleteItemConfirmationHandler = (index: number) => {
    setOpenDeleteItemConfirmation(true);
    setDeletedIndex(index);
  };

  const closeDeleteItemConfirmationHandler = () => {
    setOpenDeleteItemConfirmation(false);
    setDeletedIndex(undefined);
  };

  const deleteItemConfirmationAction = () => {
    if (deletedIndex || deletedIndex === 0) {
      const itemList = getValues("item_list");
      const deletedItem = itemList[deletedIndex];
      if (
        itemList.filter(
          (list: IPurchaseItemList) =>
            list.reference_line_item?.line_item_document_unique_id ===
            deletedItem.reference_line_item?.line_item_document_unique_id
        ).length === 1
      ) {
        const refList = getValues("reference_document_list");
        const removeRefList = refList?.filter(
          (ref) =>
            ref.document_unique_id !==
            deletedItem.reference_line_item?.line_item_document_unique_id
        );
        setValue("reference_document_list", removeRefList);
      }
      remove(deletedIndex);
    }
    closeDeleteItemConfirmationHandler();
  };

  const addItemsHandler = () => {
    const selectedItems = gridRef?.current?.api.getSelectedRows();
    if (selectedItems) {
      const document_unique_id = getValues("unique_id");

      selectedItems.forEach((sku_detail) => {
        let formatItems: IPurchaseItemList = {
          unique_id: uuidv4(),
          reference_document_type: PurchaseDocumentType.PurchaseRequest,
          reference_unique_id: document_unique_id,
          item_unique_id: sku_detail.item_unique_id || "",
          item_sku_name: sku_detail.sku_name,
          item_name: sku_detail.name,
          item_sku_desc: sku_detail.description,
          tracability: sku_detail.tracability,
          branch_id: getValues("branch_id"),
          qty: 1,
          uom_id: sku_detail.stock_uom_id,
          remark: "",
          stock_qty: 1,
          uom_name: sku_detail.stock_uom_name_th,
          uom_group: sku_detail.uom_group,
          item_sku_qty: sku_detail.item_sku_qty_list,
          item_img_url: sku_detail.img_url,
          barcode: sku_detail.barcode || "",
        };

        append(formatItems);
      });
    }
    closeItemModalHandler();
  };

  const finishSalesOrderSelect = async (data: any) => {
    if (data && data.length > 0) {
      const document_unique_id = getValues("unique_id");
      const reference_document_list =
        getValues("reference_document_list") || [];

      const { itemList } = data.reduce(
        (lists: any, so: any) => {
          so.item_list.forEach((item: any) =>
            lists.itemList.push(item.item_sku_name)
          );
          return lists;
        },
        {
          itemList: [],
        }
      );

      const { itemSkuDetailsFindManyAggrid } =
        await graphQLClientWithHeaderItem.request(ITEM_SKU_DETAIL_AGGRID, {
          aggridInput: {
            startRow: 0,
            endRow: 9999,
            filterModel: {
              sku_name: {
                filterType: "set",
                values: itemList,
              },
            },
          },
        });

      const { results: itemSkuDetails } = await itemSkuDetailsFindManyAggrid;

      data.forEach((so: any) => {
        so.item_list.forEach((sku_detail: any) => {
          if (sku_detail) {
            const foundItemIndex = itemSkuDetails.findIndex(
              (sku: any) => sku.sku_name === sku_detail.item_sku_name
            );

            const uniqueId = uuidv4();

            let formatItems: IPurchaseItemList = {
              unique_id: uniqueId,
              reference_unique_id: document_unique_id,
              reference_document_type: PurchaseDocumentType.PurchaseRequest,
              reference_line_item: {
                line_item_document_type: SalesModelType.SalesOrder,
                line_item_document_unique_id: so.unique_id,
                line_item_unique_id: sku_detail.unique_id,
              },
              item_unique_id: sku_detail.item_unique_id || "",
              item_sku_name: sku_detail.item_sku_name,
              item_name: sku_detail.item_name,
              item_sku_desc: sku_detail.item_sku_desc,
              tracability: sku_detail.tracability,
              branch_id: getValues("branch_id"),
              qty: sku_detail.qty,
              uom_id: sku_detail.uom_id,
              remark: "",
              stock_qty: sku_detail.qty,
              uom_name: sku_detail.uom_name,
              uom_group: itemSkuDetails[foundItemIndex]?.item.uom_group,
              item_sku_qty: itemSkuDetails[foundItemIndex]?.item_sku_qty,
              item_img_url: sku_detail.item_img_url,
              barcode:
                sku_detail.barcode ||
                itemSkuDetails[foundItemIndex]?.barcode ||
                "",
            };
            const itemList = getValues("item_list");

            const isNotPresent = !itemList.some(
              (item) =>
                item.reference_line_item?.line_item_document_unique_id ===
                  formatItems.reference_line_item
                    ?.line_item_document_unique_id &&
                item.item_sku_name === formatItems.item_sku_name
            );

            if (isNotPresent) append(formatItems);
          }
        });
      });

      const referenceList = [
        ...reference_document_list,
        ...data.map((so: any) => ({
          document_id: so.id,
          document_unique_id: so.unique_id,
          document_type: SalesReferenceDocumentType.SalesOrder,
        })),
      ];

      const newRef = Array.from(
        new Set(
          referenceList
            .map((doc: any) => doc.document_unique_id)
            .map((uniqueId: string) =>
              referenceList.find(
                (doc: any) => doc.document_unique_id === uniqueId
              )
            )
        )
      );

      setValue("reference_document_list", newRef);
    }
    closeSalesOrderModalHandler();
  };

  const uomOptions = (field: IPurchaseItemList) => {
    const options = (
      field.uom_group
        ? field.uom_group.convertable_uom_list &&
          field.uom_group.convertable_uom_list.length > 0
          ? [
              {
                label: field.uom_group?.base_uom?.name_th || "",
                value: field.uom_group?.base_uom?.id || "",
              },
              ...field.uom_group.convertable_uom_list.map((uom) => ({
                label: uom.name_th,
                value: uom.id,
              })),
            ]
          : [
              {
                label: field.uom_group?.base_uom?.name_th || "",
                value: field.uom_group?.base_uom?.id || "",
              },
            ]
        : []
    ) as ISelectOption[];
    return options;
  };

  const finishedStatus = [
    "approved",
    "partially_ordered",
    "fully_ordered",
  ].includes(status || "");

  return (
    <CustomizedBox
      sx={{ breakAfter: "auto" }}
      margin="2rem 0 0 0"
      maxWidth={1650}
    >
      <SubHeader title={"สินค้า"} sx={{ marginBottom: 2 }} />
      <TableContainer>
        <Table
          sx={{ minWidth: 650, overflow: "scroll" }}
          aria-label="simple table"
        >
          <TableHead>
            <TableRow>
              {headers.map((header, index) => (
                <TableCell
                  align="center"
                  key={index}
                  sx={{
                    px: 1,
                    py: 1.5,
                    backgroundColor: "#F0F1FA",
                  }}
                  width={header.width}
                >
                  <Box
                    sx={{
                      display: "flex",
                      gap: 1,
                      width: header.width,
                      justifyContent: "center",
                    }}
                  >
                    <Typography
                      fontSize={14}
                      fontWeight={600}
                      color={"primary.main"}
                    >
                      {header.thaiLabel}
                    </Typography>
                    {header.required && (
                      <Typography
                        fontSize={14}
                        color={"error.main"}
                        fontWeight={600}
                      >
                        *
                      </Typography>
                    )}
                  </Box>
                </TableCell>
              ))}
            </TableRow>
          </TableHead>
          <DragDropContext onDragEnd={dragEndHandler}>
            <Droppable droppableId="droppable" isDropDisabled={disabled}>
              {(provided) => (
                <TableBody {...provided.droppableProps} ref={provided.innerRef}>
                  {fields.map((field, index) => (
                    <Draggable
                      key={field.unique_id}
                      draggableId={"item-" + field.unique_id}
                      index={index}
                      isDragDisabled={disabled}
                    >
                      {(provided) => (
                        <TableRow
                          ref={provided.innerRef}
                          {...provided.draggableProps}
                          {...provided.dragHandleProps}
                          sx={{
                            ...provided.draggableProps.style,
                            breakInside: "avoid",
                          }}
                        >
                          <TableCell
                            align="center"
                            sx={{
                              px: 1,
                              py: 1,
                            }}
                            width={
                              (headers && headers.length > 0
                                ? headers[0]?.width || 0
                                : 0) + 16
                            }
                          >
                            <Box
                              sx={{
                                display: "flex",
                                gap: 1,
                                justifyContent: "center",
                                alignItems: "center",
                              }}
                            >
                              {!disabled && <TocIcon fontSize="small" />}
                              <Typography fontSize={14}>{index + 1}</Typography>
                            </Box>
                          </TableCell>
                          <TableCell
                            align="center"
                            sx={{
                              px: 1,
                              py: 1,
                            }}
                            width={
                              (headers && headers.length > 0
                                ? headers[1]?.width || 0
                                : 0) + 16
                            }
                          >
                            <ItemNameCell
                              control={control}
                              errors={errors}
                              field={field}
                              index={index}
                              disabled={disabled}
                              documentType={documentType}
                            />
                          </TableCell>
                          <TableCell
                            align="center"
                            sx={{
                              px: 1,
                              py: 1,
                            }}
                            width={
                              (headers && headers.length > 0
                                ? headers[2]?.width || 0
                                : 0) + 16
                            }
                          >
                            {disabled ? (
                              <Typography fontSize={14}>
                                {numberFormatter(field.qty)}
                              </Typography>
                            ) : (
                              <ControlledNumberTextField
                                control={control}
                                name={`item_list.${index}.qty`}
                                error={Boolean(
                                  errors?.item_list &&
                                    errors?.item_list[index] &&
                                    errors?.item_list[index]?.qty
                                )}
                                InputProps={{ inputProps: { min: 0 } }}
                                onChange={(value) => {
                                  const parseValue = value || 0;
                                  const currentUomId = getValues(
                                    `item_list.${index}.uom_id`
                                  );
                                  if (currentUomId) {
                                    if (field.uom_group) {
                                      if (
                                        currentUomId ===
                                        field.uom_group.base_uom?.id
                                      ) {
                                        setValue(
                                          `item_list.${index}.stock_qty`,
                                          parseValue
                                        );
                                      } else {
                                        if (
                                          field.uom_group.uom_conversion_list &&
                                          field.uom_group.uom_conversion_list
                                            .length > 0
                                        ) {
                                          const conversionUom =
                                            field.uom_group.uom_conversion_list.find(
                                              (conversion) =>
                                                conversion.target_uom_id ===
                                                currentUomId
                                            );
                                          if (conversionUom) {
                                            const targetUomQty =
                                              conversionUom.base_uom_rate /
                                              conversionUom.target_uom_rate;
                                            setValue(
                                              `item_list.${index}.stock_qty`,
                                              parseValue * targetUomQty
                                            );
                                          }
                                        }
                                      }
                                    }
                                  }
                                }}
                              />
                            )}
                          </TableCell>
                          <TableCell
                            align="center"
                            sx={{
                              px: 1,
                              py: 1,
                            }}
                            width={
                              (headers && headers.length > 0
                                ? headers[3]?.width || 0
                                : 0) + 16
                            }
                          >
                            {disabled ? (
                              <Typography fontSize={14}>
                                {field.uom_name}
                              </Typography>
                            ) : (
                              <ControlledSelect
                                control={control}
                                name={`item_list.${index}.uom_id`}
                                error={Boolean(
                                  errors?.item_list &&
                                    errors?.item_list[index] &&
                                    errors?.item_list[index]?.uom_id
                                )}
                                options={uomOptions(field)}
                                onChange={(e: any) => {
                                  const uom_name = uomOptions(field).find(
                                    (uom) => uom.value === e.target.value
                                  )?.label;
                                  setValue(
                                    `item_list.${index}.uom_name`,
                                    uom_name
                                  );
                                  const currentQty = getValues(
                                    `item_list.${index}.qty`
                                  );
                                  if (field.uom_group) {
                                    if (
                                      e.target.value ===
                                      field.uom_group.base_uom?.id
                                    ) {
                                      setValue(
                                        `item_list.${index}.stock_qty`,
                                        currentQty
                                      );
                                    } else {
                                      if (
                                        field.uom_group.uom_conversion_list &&
                                        field.uom_group.uom_conversion_list
                                          .length > 0
                                      ) {
                                        const conversionUom =
                                          field.uom_group.uom_conversion_list.find(
                                            (conversion) =>
                                              conversion.target_uom_id ===
                                              e.target.value
                                          );
                                        if (conversionUom) {
                                          const targetUomQty =
                                            conversionUom.base_uom_rate /
                                            conversionUom.target_uom_rate;
                                          setValue(
                                            `item_list.${index}.stock_qty`,
                                            currentQty * targetUomQty
                                          );
                                        }
                                      }
                                    }
                                  }
                                }}
                              />
                            )}
                          </TableCell>

                          <TableCell
                            align="center"
                            sx={{
                              px: 1,
                              py: 1,
                            }}
                            width={
                              (headers && headers.length > 0
                                ? headers[9]?.width || 0
                                : 0) + 16
                            }
                          >
                            {disabled ? (
                              <>
                                <Typography fontSize={14}>
                                  {numberFormatter(field.stock_qty)}
                                </Typography>
                                <Typography variant="caption" color="#BDBDBD">
                                  {field.uom_group?.base_uom?.name_th}
                                </Typography>
                              </>
                            ) : (
                              <ControlledTextField
                                sx={{ mt: 3 }}
                                type="number"
                                control={control}
                                name={`item_list.${index}.stock_qty`}
                                helperText={field.uom_group?.base_uom?.name_th}
                                InputProps={{
                                  inputProps: {
                                    style: { textAlign: "center" },
                                  },
                                }}
                                viewMode={true}
                              />
                            )}
                          </TableCell>
                          <TableCell
                            align="center"
                            sx={{
                              px: 1,
                              py: 1,
                            }}
                            width={
                              (headers && headers.length > 0
                                ? headers[4]?.width || 0
                                : 0) + 16
                            }
                          >
                            <Typography fontSize={14}>
                              {field.reference_line_item
                                ?.line_item_document_unique_id || "-"}
                            </Typography>
                          </TableCell>
                          {finishedStatus && (
                            <TableCell
                              align="center"
                              sx={{
                                px: 1,
                                py: 1,
                              }}
                            >
                              <Typography fontSize={14}>
                                {field.po_qty || "-"}
                              </Typography>
                            </TableCell>
                          )}
                          <TableCell
                            align="center"
                            sx={{
                              px: 1,
                              py: 1,
                            }}
                            width={
                              (headers && headers.length > 0
                                ? headers[10]?.width || 0
                                : 0) + 16
                            }
                          >
                            {disabled ? (
                              <Typography fontSize={14}>
                                {field.remark}
                              </Typography>
                            ) : (
                              <ControlledTextField
                                control={control}
                                name={`item_list.${index}.remark`}
                                error={Boolean(
                                  errors?.item_list &&
                                    errors?.item_list[index] &&
                                    errors?.item_list[index]?.remark
                                )}
                              />
                            )}
                          </TableCell>
                          {!disabled && (
                            <TableCell
                              align="center"
                              sx={{
                                px: 1,
                                py: 1,
                              }}
                              width={
                                (headers && headers.length > 0
                                  ? headers[11]?.width || 0
                                  : 0) + 16
                              }
                            >
                              <IconButton
                                aria-label="delete"
                                size="small"
                                sx={{ ml: 1, color: "rgba(189, 189, 189, 1)" }}
                                onClick={() =>
                                  openDeleteItemConfirmationHandler(index)
                                }
                              >
                                <CloseIcon fontSize="small" />
                              </IconButton>
                            </TableCell>
                          )}
                        </TableRow>
                      )}
                    </Draggable>
                  ))}
                  {provided.placeholder}
                </TableBody>
              )}
            </Droppable>
          </DragDropContext>
        </Table>
      </TableContainer>
      {!disabled && (
        <ButtonLayout sx={{ marginTop: 2 }}>
          <CustomizedButton
            title="เพิ่มสินค้า"
            variant="outlined"
            onClick={openItemModalHandler}
            addIcon
          />
          <CustomizedButton
            title="นำเข้าใบสั่งขาย"
            variant="outlined"
            onClick={openSalesOrderModalHandler}
            addIcon
          />
        </ButtonLayout>
      )}
      <Confirmation
        title="ยืนยันการลบสินค้า"
        open={openDeleteItemConfirmation}
        handleClose={closeDeleteItemConfirmationHandler}
        action={deleteItemConfirmationAction}
      />
      <ItemTableModal
        ref={gridRef}
        itemModal={itemModal}
        closeItemModalHandler={closeItemModalHandler}
        addItemsHandler={addItemsHandler}
        purchasableItem
      />
      <CheckedSalesOrderModal
        showSelectItem={salesOrderModal}
        closeItemTable={closeSalesOrderModalHandler}
        finishItemsSelect={finishSalesOrderSelect}
        selectedIds={selectedIds}
        setSelectedIds={setSelectedIds}
        idsSnapshot={idsSnapshot}
        setIdsSnapshot={setIdsSnapshot}
        rowSelection={"multiple"}
      />
    </CustomizedBox>
  );
};

export default PurchaseRequestItemList;
